// Repeating entries: journal templates that post themselves on a rhythm
// (rent, subscriptions, retainers). The run posts every template that's due;
// pausing a template takes it out of the run without deleting it.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { AccountPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import {
  createRecurringJournalTemplate,
  generateRecurringJournals,
  listRecurringJournalTemplates,
  type RecurringJournalTemplate,
} from "@/lib/api";
import { frequencyLabel, humanizeLabel } from "@/lib/labels";

const FREQUENCIES = [
  "weekly",
  "fortnightly",
  "monthly",
  "quarterly",
  "semi_annual",
  "annual",
] as const;

type Frequency = (typeof FREQUENCIES)[number];

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "completed":
      return "success";
    case "active":
      return "info";
    case "paused":
      return "warning";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(value: string | undefined): string {
  return value ? new Date(value).toLocaleDateString() : "—";
}

function templateTotal(template: RecurringJournalTemplate): number {
  return template.lines.reduce((sum, line) => sum + line.debit_minor, 0);
}

// The create form's draft lives in the page (not in the generic
// document-draft mirror, which can't model balanced journal lines) so
// closing and reopening the drawer keeps every line.
interface LineDraft {
  uid: string;
  accountId: string;
  description: string;
  side: "debit" | "credit";
  amount: string;
}

function emptyLine(side: "debit" | "credit"): LineDraft {
  return {
    uid: crypto.randomUUID(),
    accountId: "",
    description: "",
    side,
    amount: "",
  };
}

interface TemplateDraft {
  name: string;
  frequency: Frequency;
  startDate: string;
  lines: LineDraft[];
}

function newTemplateDraft(): TemplateDraft {
  return {
    name: "",
    frequency: "monthly",
    startDate: todayInput(),
    lines: [emptyLine("debit"), emptyLine("credit")],
  };
}

function lineMinor(line: LineDraft): number | null {
  const trimmed = line.amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

function draftTotals(lines: LineDraft[]): { debit: number; credit: number } {
  let debit = 0;
  let credit = 0;
  for (const line of lines) {
    const minor = lineMinor(line);
    if (minor === null) continue;
    if (line.side === "debit") debit += minor;
    else credit += minor;
  }
  return { debit, credit };
}

function describeRunResult(generatedCount: number): string {
  if (generatedCount === 0)
    return "Nothing was due — every template is up to date.";
  return `Posted ${generatedCount} due ${generatedCount === 1 ? "entry" : "entries"}.`;
}

export function RepeatingEntriesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<RecurringJournalTemplate[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);
  const [drawer, setDrawer] = useState(false);

  // Client-side table controls over the fetched list (the list endpoint has
  // no search/status params — filtering happens here).
  const [statusTab, setStatusTab] = useState("");
  const [search, setSearch] = useState("");

  const [draft, setDraft] = useState<TemplateDraft>(newTemplateDraft);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const reload = () => {
    listRecurringJournalTemplates()
      .then((rows) => {
        setTemplates(rows);
        setError(null);
      })
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  };

  useEffect(reload, []);

  const runDue = async () => {
    setRunning(true);
    setError(null);
    setNotice(null);
    try {
      const result = await generateRecurringJournals();
      setNotice(describeRunResult(result.generated_count));
      setConfirmRun(false);
      reload();
    } catch (cause) {
      setError(`Run failed: ${toErrorMessage(cause)}`);
      setConfirmRun(false);
    } finally {
      setRunning(false);
    }
  };

  const updateDraft = (patch: Partial<TemplateDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const updateLine = (uid: string, patch: Partial<LineDraft>) =>
    setDraft((prev) => ({
      ...prev,
      lines: prev.lines.map((line) =>
        line.uid === uid ? { ...line, ...patch } : line,
      ),
    }));

  const removeLine = (uid: string) =>
    setDraft((prev) => ({
      ...prev,
      lines: prev.lines.filter((line) => line.uid !== uid),
    }));

  const resetDraft = () => {
    setDraft(newTemplateDraft());
    setFormError(null);
  };

  const totals = draftTotals(draft.lines);
  const balanced = totals.debit === totals.credit;
  const offBy = Math.abs(totals.debit - totals.credit);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (!draft.name.trim()) throw new Error("Name is required.");
      if (!draft.startDate) throw new Error("First date is required.");
      if (draft.lines.length < 2)
        throw new Error("A balanced template needs at least two lines.");
      let debits = 0;
      let credits = 0;
      const payload = draft.lines.map((line) => {
        if (!line.accountId) throw new Error("Every line needs an account.");
        if (!line.description.trim())
          throw new Error("Every line needs a description.");
        const minor = lineMinor(line);
        if (minor === null)
          throw new Error("Every line amount must be a positive number.");
        if (line.side === "debit") debits += minor;
        else credits += minor;
        return {
          account_id: line.accountId,
          description: line.description.trim(),
          debit_minor: line.side === "debit" ? minor : 0,
          credit_minor: line.side === "credit" ? minor : 0,
        };
      });
      if (debits !== credits)
        throw new Error(
          "Debits and credits must be equal before the template can be saved.",
        );
      const created = await createRecurringJournalTemplate({
        name: draft.name.trim(),
        frequency: draft.frequency,
        start_date: draft.startDate,
        lines: payload,
      });
      resetDraft();
      setDrawer(false);
      reload();
      void navigate({
        to: "/recurring-journals/$templateId",
        params: { templateId: created.id },
      });
    } catch (cause) {
      setFormError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const statusCounts = (templates ?? []).reduce<Record<string, number>>(
    (counts, template) => {
      const key = template.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  const statusTabs = Object.keys(statusCounts).sort();

  const query = search.trim().toLowerCase();
  const visibleTemplates = (templates ?? [])
    .filter(
      (template) => !statusTab || template.status.toLowerCase() === statusTab,
    )
    .filter((template) => {
      if (!query) return true;
      return (
        template.name.toLowerCase().includes(query) ||
        (template.reference ?? "").toLowerCase().includes(query) ||
        frequencyLabel(template.frequency).toLowerCase().includes(query)
      );
    });

  const actions = (
    <>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() => setConfirmRun(true)}
        disabled={running}
        title="Post every due occurrence across the whole organization (recurring-journal run)"
      >
        {running ? "Running…" : "Run what's due"}
      </Button>
      <Button type="button" size="sm" onClick={() => setDrawer(true)}>
        New template
      </Button>
    </>
  );

  return (
    <div className="grid gap-6">
      {embedded ? (
        <div className="actions-row">{actions}</div>
      ) : (
        <PageHeader
          title="Repeating entries"
          description="Templates that post themselves on a rhythm — run what's due in one step."
          actions={actions}
        />
      )}

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      <Tabs
        label="Filter repeating entries by status"
        tabs={[
          { value: "", label: `All (${(templates ?? []).length})` },
          ...statusTabs.map((status) => ({
            value: status,
            label: `${plainStatus(status)} (${statusCounts[status]})`,
          })),
        ]}
        value={statusTab}
        onValueChange={setStatusTab}
      >
        <div className="surface grid gap-3 rounded-2xl p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label
              htmlFor="repeating-search"
              className="grid min-w-40 flex-1 gap-1 text-xs font-medium"
            >
              Search
              <Input
                id="repeating-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name or reference…"
              />
            </label>
            <Button type="button" variant="secondary" onClick={reload}>
              Refresh
            </Button>
          </div>
        </div>

        <div
          className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
          data-testid="repeating-table"
        >
          <div className="table-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>How often</TableHead>
                  <TableHead>Next run</TableHead>
                  <TableHead className="text-right">Postings so far</TableHead>
                  <TableHead className="text-right">Per posting</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates === null ? (
                  <TableRow>
                    <TableCell colSpan={6}>Loading…</TableCell>
                  </TableRow>
                ) : templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      No repeating entries yet — create the first template
                      above.
                    </TableCell>
                  </TableRow>
                ) : visibleTemplates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      No templates match these filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>
                        <Link
                          to="/recurring-journals/$templateId"
                          params={{ templateId: template.id }}
                          className="font-medium hover:underline"
                        >
                          {template.name}
                        </Link>
                        <span className="block text-xs text-[var(--muted-foreground)]">
                          {template.reference
                            ? humanizeLabel(template.reference)
                            : null}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {frequencyLabel(template.frequency)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {formatDate(template.next_run_date)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {template.occurrences_generated}
                        {template.max_occurrences
                          ? ` of ${template.max_occurrences}`
                          : ""}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney({
                          cents: templateTotal(template),
                          currency: "USD",
                        })}
                      </TableCell>
                      <TableCell>
                        <StatusDot
                          tone={statusTone(template.status)}
                          label={plainStatus(template.status)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div
          className="stack-list grid gap-3 min-[64rem]:hidden"
          data-testid="repeating-stack"
        >
          {templates === null ? (
            <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
          ) : visibleTemplates.length === 0 ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              {templates.length === 0
                ? "No repeating entries yet — create the first template."
                : "No templates match these filters."}
            </p>
          ) : (
            visibleTemplates.map((template) => (
              <article
                key={template.id}
                className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
              >
                <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                  <Link
                    to="/recurring-journals/$templateId"
                    params={{ templateId: template.id }}
                    className="record-title underline-offset-4 hover:underline"
                  >
                    {template.name}
                  </Link>
                  <span className="tabular-nums">
                    {formatMoney({
                      cents: templateTotal(template),
                      currency: "USD",
                    })}
                  </span>
                </div>
                <CardField label="How often">
                  {frequencyLabel(template.frequency)}
                </CardField>
                <CardField label="Next run">
                  {formatDate(template.next_run_date)}
                </CardField>
                <CardField label="Status">
                  <StatusDot
                    tone={statusTone(template.status)}
                    label={plainStatus(template.status)}
                  />
                </CardField>
                <CardField label="Postings so far">
                  {template.occurrences_generated}
                  {template.max_occurrences
                    ? ` of ${template.max_occurrences}`
                    : ""}
                </CardField>
                <div className="flex flex-wrap gap-2 pt-3">
                  <Button asChild variant="secondary" size="sm">
                    <Link
                      to="/recurring-journals/$templateId"
                      params={{ templateId: template.id }}
                    >
                      Open template
                    </Link>
                  </Button>
                </div>
              </article>
            ))
          )}
        </div>
      </Tabs>

      <Drawer
        open={drawer}
        onOpenChange={setDrawer}
        title="New repeating entry"
        description="Saves a balanced template. Run what's due posts it on the schedule; nothing posts until then. Closing this panel keeps your draft."
        className="w-[min(46rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDrawer(false)}
            >
              Back to templates
            </Button>
            {draft.name ||
            draft.lines.some((line) => line.description || line.amount) ? (
              <Button type="button" variant="ghost" onClick={resetDraft}>
                Discard draft
              </Button>
            ) : null}
            <Button
              type="submit"
              form="repeating-create-form"
              disabled={saving || !balanced}
              className="ms-auto"
            >
              {saving ? "Creating…" : "Create template"}
            </Button>
          </>
        }
      >
        <form
          id="repeating-create-form"
          onSubmit={submit}
          className="grid gap-3"
          aria-label="Create repeating entry"
        >
          {formError ? (
            <p role="alert" className="text-sm text-[var(--danger)]">
              {formError}
            </p>
          ) : null}
          <label
            htmlFor="repeat-name"
            className="grid gap-1 text-xs font-medium"
          >
            Name
            <Input
              id="repeat-name"
              value={draft.name}
              onChange={(event) => updateDraft({ name: event.target.value })}
              required
              disabled={saving}
            />
          </label>
          <label
            htmlFor="repeat-frequency"
            className="grid gap-1 text-xs font-medium"
          >
            How often
            <select
              id="repeat-frequency"
              className="select"
              value={draft.frequency}
              onChange={(event) =>
                updateDraft({ frequency: event.target.value as Frequency })
              }
              disabled={saving}
            >
              {FREQUENCIES.map((item) => (
                <option key={item} value={item}>
                  {frequencyLabel(item)}
                </option>
              ))}
            </select>
          </label>
          <label
            htmlFor="repeat-start"
            className="grid gap-1 text-xs font-medium"
          >
            First date
            <Input
              id="repeat-start"
              type="date"
              value={draft.startDate}
              onChange={(event) =>
                updateDraft({ startDate: event.target.value })
              }
              required
              disabled={saving}
            />
          </label>
          <fieldset className="grid gap-2">
            <legend className="text-xs font-medium">
              What each posting writes
            </legend>
            {draft.lines.map((line, index) => (
              <div
                key={line.uid}
                className="grid gap-2 rounded-xl border border-[var(--border)] p-3"
              >
                <p className="text-xs font-medium">Line {index + 1}</p>
                <label
                  className="grid gap-1 text-xs font-medium"
                  htmlFor={`repeat-line-${index}-account`}
                >
                  Account
                  <AccountPicker
                    id={`repeat-line-${index}-account`}
                    value={line.accountId}
                    onChange={(accountId) => updateLine(line.uid, { accountId })}
                    required
                    disabled={saving}
                  />
                </label>
                <label
                  className="grid gap-1 text-xs font-medium"
                  htmlFor={`repeat-line-${index}-description`}
                >
                  Description
                  <Input
                    id={`repeat-line-${index}-description`}
                    value={line.description}
                    onChange={(event) =>
                      updateLine(line.uid, { description: event.target.value })
                    }
                    required
                    disabled={saving}
                  />
                </label>
                <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                  <label
                    className="grid gap-1 text-xs font-medium"
                    htmlFor={`repeat-line-${index}-side`}
                  >
                    Side
                    <select
                      id={`repeat-line-${index}-side`}
                      className="select"
                      value={line.side}
                      onChange={(event) =>
                        updateLine(line.uid, {
                          side: event.target.value as "debit" | "credit",
                        })
                      }
                      disabled={saving}
                    >
                      <option value="debit">Debit (in)</option>
                      <option value="credit">Credit (out)</option>
                    </select>
                  </label>
                  <label
                    className="grid gap-1 text-xs font-medium"
                    htmlFor={`repeat-line-${index}-amount`}
                  >
                    Amount (USD)
                    <Input
                      id={`repeat-line-${index}-amount`}
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={line.amount}
                      onChange={(event) =>
                        updateLine(line.uid, { amount: event.target.value })
                      }
                      required
                      disabled={saving}
                    />
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLine(line.uid)}
                    disabled={saving || draft.lines.length <= 2}
                    aria-label={`Remove line ${index + 1}`}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    lines: [...prev.lines, emptyLine("debit")],
                  }))
                }
                disabled={saving}
              >
                Add line
              </Button>
            </div>
          </fieldset>
          <p role="status" aria-live="polite" className="text-sm">
            {balanced ? (
              <span className="text-[var(--muted-foreground)]">
                Balanced — debits{" "}
                {formatMoney({ cents: totals.debit, currency: "USD" })} =
                credits {formatMoney({ cents: totals.credit, currency: "USD" })}.
              </span>
            ) : (
              <span className="font-medium text-[var(--danger)]">
                Unbalanced — debits{" "}
                {formatMoney({ cents: totals.debit, currency: "USD" })}, credits{" "}
                {formatMoney({ cents: totals.credit, currency: "USD" })} (off by{" "}
                {formatMoney({ cents: offBy, currency: "USD" })}). Debits and
                credits must be equal before saving.
              </span>
            )}
          </p>
        </form>
      </Drawer>

      <Dialog
        open={confirmRun}
        onOpenChange={(open) => {
          if (!open && !running) setConfirmRun(false);
        }}
        title="Run all due templates?"
        description="Posts a journal for every repeating entry due as of today — across the whole organization, not just one template."
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmRun(false)}
            disabled={running}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void runDue()}
            disabled={running}
          >
            {running ? "Running…" : "Run all due templates"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
